import { Plus } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import MainLayout from '../components/layout/MainLayout'
import LoadingSpinner from '../components/shared/LoadingSpinner'

import { API_URL, apiFetch } from '../config/api'
interface Project {
  id: number
  title: string
  description: string
  project_type: string
  visibility: string
  status: string
  created_at: string
  agents_count?: number
  tasks_count?: number
  budget?: number
}

type TabType = 'research' | 'mission'

export default function MyProjects() {
  const navigate = useNavigate()
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<TabType>('research')

  useEffect(() => {
    void loadProjects()
  }, [])

  const loadProjects = async () => {
    try {
      const token = localStorage.getItem('token')
      if (!token) {
        console.error('No token found')
        setLoading(false)
        return
      }
      const res = await apiFetch(`${API_URL}/api/users/me/projects`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (res.ok) {
        const data = await res.json()
        console.log('My projects data:', data)
        setProjects((Array.isArray(data) ? data : (data.projects || [])) as Project[])
      } else {
        console.error('Failed to load projects:', res.status)
      }
    } catch (err) {
      console.error('Load projects error:', err)
    } finally {
      setLoading(false)
    }
  }

  const filteredProjects = projects.filter(p => p.project_type === activeTab)

  return (
    <MainLayout>
      <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '32px'
        }}>
          <div>
            <h1 style={{
              fontSize: '32px',
              fontWeight: '800',
              color: 'var(--text-primary)',
              marginBottom: '8px'
            }}>
              My Projects
            </h1>
            <p style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
              Manage your research and mission projects
            </p>
          </div>
          <button
            onClick={() => navigate('/projects/create')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 24px',
              background: 'linear-gradient(135deg, #667eea, #764ba2)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'transform 0.2s',
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
          >
            <Plus size={18} />
            Create Project
          </button>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          gap: '12px',
          marginBottom: '24px',
          borderBottom: '2px solid #e2e8f0',
          paddingBottom: '0'
        }}>
          <button
            onClick={() => setActiveTab('research')}
            style={{
              padding: '12px 24px',
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === 'research' ? '3px solid #8b5cf6' : '3px solid transparent',
              color: activeTab === 'research' ? '#8b5cf6' : 'var(--text-secondary)',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s',
              marginBottom: '-2px'
            }}
          >
            🔬 Research ({projects.filter(p => p.project_type === 'research').length})
          </button>
          <button
            onClick={() => setActiveTab('mission')}
            style={{
              padding: '12px 24px',
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === 'mission' ? '3px solid #3b82f6' : '3px solid transparent',
              color: activeTab === 'mission' ? '#3b82f6' : 'var(--text-secondary)',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s',
              marginBottom: '-2px'
            }}
          >
            🎯 Mission ({projects.filter(p => p.project_type === 'mission').length})
          </button>
        </div>

        {/* Content */}
        {loading ? (
          <LoadingSpinner />
        ) : filteredProjects.length > 0 ? (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
            gap: '24px'
          }}>
            {filteredProjects.map(project => (
              <div
                key={project.id}
                onClick={() => navigate(`/project/${project.id}`)}
                style={{
                  padding: '24px',
                  background: 'var(--card-bg)',
                  borderRadius: '16px',
                  cursor: 'pointer',
                  border: '1px solid #e2e8f0',
                  transition: 'all 0.2s',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)'
                  e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.15)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)'
                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)'
                }}
              >
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                  <div style={{ fontSize: '40px' }}>
                    {project.project_type === 'research' ? '🔬' : '🎯'}
                  </div>
                  <div style={{ flex: 1 }}>
                    <h3 style={{
                      fontSize: '18px',
                      fontWeight: '700',
                      color: 'var(--text-primary)',
                      marginBottom: '4px',
                      lineHeight: '1.3'
                    }}>
                      {project.title}
                    </h3>
                  </div>
                </div>

                {/* Description */}
                <p style={{
                  fontSize: '14px',
                  color: 'var(--text-secondary)',
                  lineHeight: '1.6',
                  marginBottom: '16px',
                  minHeight: '60px'
                }}>
                  {project.description?.substring(0, 120)}
                  {project.description && project.description.length > 120 ? '...' : ''}
                </p>

                {/* Badges */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
                  <span style={{
                    padding: '6px 12px',
                    background: project.project_type === 'research'
                      ? 'rgba(139, 92, 246, 0.1)'
                      : 'rgba(59, 130, 246, 0.1)',
                    color: project.project_type === 'research' ? '#8b5cf6' : '#3b82f6',
                    borderRadius: '8px',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>
                    {project.project_type === 'research' ? 'Research' : 'Mission'}
                  </span>
                  <span style={{
                    padding: '6px 12px',
                    background: project.status === 'active'
                      ? 'rgba(34, 197, 94, 0.1)'
                      : 'rgba(100, 116, 139, 0.1)',
                    color: project.status === 'active' ? '#22c55e' : 'var(--text-secondary)',
                    borderRadius: '8px',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>
                    {project.status}
                  </span>
                  <span style={{
                    padding: '6px 12px',
                    background: 'rgba(251, 146, 60, 0.1)',
                    color: '#fb923c',
                    borderRadius: '8px',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>
                    {project.visibility}
                  </span>
                </div>

                {/* Stats */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  paddingTop: '16px',
                  borderTop: '1px solid #e2e8f0'
                }}>
                  <div style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>
                    {project.agents_count || 0} agents • {project.tasks_count || 0} tasks
                  </div>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '700',
                    color: '#10b981'
                  }}>
                    {project.budget || 0} FLZ
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{
            textAlign: 'center',
            padding: '80px 24px',
            background: 'var(--card-bg)',
            borderRadius: '16px',
            border: '2px dashed #e2e8f0'
          }}>
            <div style={{ fontSize: '64px', marginBottom: '16px' }}>
              {activeTab === 'research' ? '🔬' : '🎯'}
            </div>
            <h3 style={{
              fontSize: '20px',
              fontWeight: '700',
              color: 'var(--text-primary)',
              marginBottom: '8px'
            }}>
              No {activeTab} projects yet
            </h3>
            <p style={{
              fontSize: '14px',
              color: 'var(--text-secondary)',
              marginBottom: '24px'
            }}>
              Create your first {activeTab} project to get started
            </p>
            <button
              onClick={() => navigate('/projects/create')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 24px',
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600'
              }}
            >
              <Plus size={18} />
              Create Project
            </button>
          </div>
        )}
      </div>
    </MainLayout>
  )
}
